export type SubscriptionFeature =
  | 'dashboard'
  | 'citas'
  | 'ordenes'
  | 'inventario'
  | 'clientes'
  | 'vehiculos'
  | 'cotizaciones'
  | 'recordatorios'
  | 'settings_general'
  | 'settings';

export type OnboardingStep = 'customer' | 'vehicle' | 'work_order' | 'dashboard' | 'completed';

export type OnboardingStatus = {
  current_step: OnboardingStep;
  steps: {
    customer: boolean;
    vehicle: boolean;
    work_order: boolean;
    dashboard: boolean;
  };
  completed_at: string | null;
};

export type TallerSubscriptionContext = {
  id: number;
  name: string;
  status: string;
  plan_id: number | null;
  plan_name: string | null;
  plan_price: number | null;
  max_clientes: number | null;
  max_vehiculos: number | null;
  max_usuarios: number | null;
  subscription_status: string | null;
  billing_cycle: string | null;
  current_period_end: string | null;
  next_invoice_at: string | null;
  trial_ends_at: string | null;
  trial_start_date: string | null;
  trial_end_date: string | null;
  is_trial_active: boolean;
  activated_at: string | null;
  trial_day_number: number | null;
  trial_days_left: number | null;
  onboarding_status: OnboardingStatus;
};

export type FeatureAccessResult = {
  allowed: boolean;
  reason: 'plan' | 'billing' | null;
  title: string | null;
  description: string | null;
};

export type OnboardingStepDefinition = {
  key: Exclude<OnboardingStep, 'completed'>;
  title: string;
  description: string;
  ctaLabel: string;
  href: string;
};

export type OnboardingProgressStep = OnboardingStepDefinition & {
  completed: boolean;
  isCurrent: boolean;
  index: number;
};

export type OnboardingProgressSummary = {
  steps: OnboardingProgressStep[];
  completedSteps: number;
  totalSteps: number;
  progressPercent: number;
  currentStep: OnboardingStep;
  isCompleted: boolean;
  nextActionHref: string;
  nextActionLabel: string;
};

export type SubscriptionBannerTone = 'rose' | 'amber' | 'sky' | 'emerald';

export type SubscriptionBannerContent = {
  tone: SubscriptionBannerTone;
  title: string;
  description: string;
  badge?: string | null;
  actionLabel?: string | null;
  actionHref?: string | null;
};

export const FEATURE_LABELS: Record<SubscriptionFeature, string> = {
  dashboard: 'Dashboard',
  citas: 'Citas',
  ordenes: 'Órdenes',
  inventario: 'Inventario',
  clientes: 'Clientes',
  vehiculos: 'Vehículos',
  cotizaciones: 'Cotizaciones',
  recordatorios: 'Recordatorios',
  settings_general: 'Configuración general',
  settings: 'Configuración',
};

const BASIC_PLAN_FEATURES: SubscriptionFeature[] = [
  'dashboard',
  'citas',
  'ordenes',
  'clientes',
  'vehiculos',
  'settings_general',
];

const ALL_PLAN_FEATURES: SubscriptionFeature[] = [
  'dashboard',
  'citas',
  'ordenes',
  'inventario',
  'clientes',
  'vehiculos',
  'cotizaciones',
  'recordatorios',
  'settings_general',
  'settings',
];

const BILLING_RECOVERY_FEATURES = new Set<SubscriptionFeature>(['dashboard', 'settings_general', 'settings']);
const BASIC_PLAN_NAMES = new Set(['basico', 'basic', 'base']);

export const ONBOARDING_STEP_DEFINITIONS: OnboardingStepDefinition[] = [
  {
    key: 'customer',
    title: 'Agrega tu primer cliente',
    description: 'Empieza por quien trae el vehículo. Así tendrás todo en orden desde el inicio.',
    ctaLabel: 'Crear cliente',
    href: '/admin/clientes?new=true',
  },
  {
    key: 'vehicle',
    title: 'Registra el vehículo',
    description: 'Guarda placa, marca y modelo para identificarlo rápido cada vez que vuelva al taller.',
    ctaLabel: 'Agregar vehículo',
    href: '/admin/vehiculos?new=true',
  },
  {
    key: 'work_order',
    title: 'Abre tu primera orden',
    description: 'Deja lista la recepción del trabajo para que tu equipo sepa qué hacer.',
    ctaLabel: 'Crear orden',
    href: '/admin/ordenes?directa=true',
  },
  {
    key: 'dashboard',
    title: 'Listo, ya tienes tu taller en marcha',
    description: 'Ahora puedes ver clientes, vehículos y órdenes en un solo lugar.',
    ctaLabel: 'Ir al dashboard',
    href: '/admin',
  },
];

const normalizeText = (value: string | null | undefined) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const getDefaultOnboardingStatus = (): OnboardingStatus => ({
  current_step: 'customer',
  steps: {
    customer: false,
    vehicle: false,
    work_order: false,
    dashboard: false,
  },
  completed_at: null,
});

const normalizeOnboardingStatus = (status: OnboardingStatus | null | undefined): OnboardingStatus => {
  if (!status) return getDefaultOnboardingStatus();

  return {
    current_step: status.current_step || 'customer',
    steps: {
      customer: status.steps?.customer === true,
      vehicle: status.steps?.vehicle === true,
      work_order: status.steps?.work_order === true,
      dashboard: status.steps?.dashboard === true,
    },
    completed_at: status.completed_at || null,
  };
};

const getOnboardingStepDefinition = (step: OnboardingStep) =>
  ONBOARDING_STEP_DEFINITIONS.find((item) => item.key === step) || ONBOARDING_STEP_DEFINITIONS[0];

export const getNormalizedWorkshopStatus = (status: string | null | undefined) => {
  const normalized = normalizeText(status);
  return normalized || 'activo';
};

export const getNormalizedPlanName = (planName: string | null | undefined) => normalizeText(planName);

export const isBasicPlan = (context: TallerSubscriptionContext | null | undefined) =>
  BASIC_PLAN_NAMES.has(getNormalizedPlanName(context?.plan_name));

export const isBillingRestrictedStatus = (status: string | null | undefined) => {
  const normalized = getNormalizedWorkshopStatus(status);
  return normalized === 'suspendido' || normalized === 'vencido';
};

export const getStatusLabel = (status: string | null | undefined) => {
  const normalized = getNormalizedWorkshopStatus(status);
  if (normalized === 'trial') return 'Trial';
  if (normalized === 'en_riesgo') return 'En riesgo';
  if (normalized === 'vencido') return 'Vencido';
  if (normalized === 'suspendido') return 'Suspendido';
  return 'Activo';
};

export const getPlanLabel = (context: TallerSubscriptionContext | null | undefined) =>
  context?.plan_name?.trim() || 'Sin plan';

export const getFeaturesForPlan = (context: TallerSubscriptionContext | null | undefined) =>
  isBasicPlan(context) ? BASIC_PLAN_FEATURES : ALL_PLAN_FEATURES;

export const getFeatureAccess = (
  context: TallerSubscriptionContext | null | undefined,
  feature: SubscriptionFeature
): FeatureAccessResult => {
  if (!context) {
    return { allowed: true, reason: null, title: null, description: null };
  }

  if (isBillingRestrictedStatus(context.status) && !BILLING_RECOVERY_FEATURES.has(feature)) {
    return {
      allowed: false,
      reason: 'billing',
      title: 'Tu suscripción necesita regularización',
      description:
        'Tu taller está temporalmente limitado por el estado de cobro. Dejamos disponibles el dashboard y la configuración para ayudarte a recuperar la operación.',
    };
  }

  if (!getFeaturesForPlan(context).includes(feature)) {
    return {
      allowed: false,
      reason: 'plan',
      title: `${FEATURE_LABELS[feature]} disponible en un plan superior`,
      description:
        'Tu plan actual cubre la operación esencial del taller. Si quieres habilitar este módulo, necesitas subir a un plan con herramientas avanzadas.',
    };
  }

  return { allowed: true, reason: null, title: null, description: null };
};

export const getOnboardingProgress = (
  context: TallerSubscriptionContext | null | undefined
): OnboardingProgressSummary => {
  const onboardingStatus = normalizeOnboardingStatus(context?.onboarding_status);
  const steps = ONBOARDING_STEP_DEFINITIONS.map((step, index) => ({
    ...step,
    completed: onboardingStatus.steps[step.key] === true,
    isCurrent: onboardingStatus.current_step === step.key,
    index,
  }));
  const completedSteps = steps.filter((step) => step.completed).length;
  const totalSteps = steps.length;
  const isCompleted = onboardingStatus.current_step === 'completed' || onboardingStatus.completed_at !== null;
  const currentDefinition = getOnboardingStepDefinition(onboardingStatus.current_step);

  return {
    steps,
    completedSteps,
    totalSteps,
    progressPercent: Math.round((completedSteps / totalSteps) * 100),
    currentStep: onboardingStatus.current_step,
    isCompleted,
    nextActionHref: isCompleted ? '/admin' : currentDefinition.href,
    nextActionLabel: isCompleted ? 'Ir al dashboard' : currentDefinition.ctaLabel,
  };
};

export const getSubscriptionBannerContent = (
  context: TallerSubscriptionContext | null | undefined
): SubscriptionBannerContent | null => {
  if (!context) return null;

  const status = getNormalizedWorkshopStatus(context.status);
  const onboarding = getOnboardingProgress(context);
  const trialDayNumber = Math.max(1, Math.min(14, context.trial_day_number || 1));
  const trialDaysLeft = Math.max(0, context.trial_days_left || 0);
  const isExpiredTrial =
    !!context.trial_start_date &&
    !!context.trial_end_date &&
    !context.is_trial_active &&
    context.subscription_status === 'past_due';

  if (status === 'suspendido' || status === 'vencido') {
    if (isExpiredTrial) {
      return {
        tone: 'rose',
        title: 'Tu prueba ha terminado',
        description: 'Activa un plan para recuperar el control de tu taller.',
        badge: 'Acceso limitado',
        actionLabel: 'Revisar suscripción',
        actionHref: '/admin/subscription',
      };
    }

    return {
      tone: 'rose',
      title: status === 'vencido' ? 'Suscripción vencida' : 'Suscripción suspendida',
      description:
        'Solo dejamos disponibles el dashboard y la configuración para que el taller pueda revisar su estado y regularizar el acceso.',
      actionLabel: 'Regularizar acceso',
      actionHref: '/admin/subscription',
    };
  }

  if (status === 'en_riesgo') {
    return {
      tone: 'amber',
      title: 'Suscripción en riesgo',
      description:
        'Tu taller sigue operativo, pero conviene regularizar el cobro pronto para evitar una suspensión automática.',
      actionLabel: 'Revisar suscripción',
      actionHref: '/admin/subscription',
    };
  }

  if (status === 'trial') {
    if (trialDaysLeft === 1) {
      return {
        tone: 'rose',
        title: 'Tu prueba vence hoy',
        description: 'Activa tu plan hoy mismo para no perder continuidad ni acceso al plan Pro.',
        badge: 'Ultimo dia de prueba',
        actionLabel: 'Elegir mi plan',
        actionHref: '/admin/upgrade',
      };
    }

    if (trialDaysLeft === 3) {
      return {
        tone: 'amber',
        title: 'Tu prueba Pro entra en cuenta regresiva',
        description: 'Te quedan 3 dias para activar un plan y seguir con acceso completo sin interrupciones.',
        badge: '3 dias restantes',
        actionLabel: onboarding.isCompleted ? 'Elegir mi plan' : 'Seguir configurando',
        actionHref: onboarding.isCompleted ? '/admin/upgrade' : onboarding.nextActionHref,
      };
    }

    return {
      tone: 'sky',
      title: `Prueba Pro activa: dia ${trialDayNumber} de 14`,
      description: 'Tienes acceso completo al plan Pro durante la prueba. Sigue avanzando para dejar tu taller listo antes del vencimiento.',
      badge: `${trialDaysLeft} día${trialDaysLeft === 1 ? '' : 's'} por delante`,
      actionLabel: onboarding.isCompleted ? 'Ir al dashboard' : 'Continuar onboarding',
      actionHref: onboarding.isCompleted ? '/admin' : onboarding.nextActionHref,
    };
  }

  if (isBasicPlan(context)) {
    return {
      tone: 'emerald',
      title: 'Plan Básico activo',
      description:
        'El taller tiene acceso a la operación esencial. La configuración general sigue disponible y los módulos avanzados se mantienen visibles con bloqueo para facilitar el upsell.',
    };
  }

  return null;
};
